import React, { useState, useRef, useEffect } from 'react';

/**
 * OTP GENERATOR - PART 1
 *
 * See EVERYTHING about random number generation
 * From entropy source to final 6-digit code
 */

interface GenerationEvent {
  id: string;
  timestamp: number;
  layer: 'entropy' | 'random' | 'crypto' | 'math' | 'format' | 'validate';
  type: 'info' | 'success' | 'process' | 'detail';
  message: string;
  details?: any;
  duration?: number;
}

export default function OTPGenerator() {
  const [events, setEvents] = useState<GenerationEvent[]>([]);
  const [currentCode, setCurrentCode] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [stats, setStats] = useState({
    totalGenerated: 0,
    averageTime: 0,
    entropy: 0,
    lastCode: ''
  });
  const eventsEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    eventsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [events]);

  const logEvent = (event: Omit<GenerationEvent, 'id' | 'timestamp'>) => {
    const newEvent: GenerationEvent = {
      ...event,
      id: `evt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now()
    };

    setEvents(prev => [...prev, newEvent]);
    return new Promise(resolve => setTimeout(resolve, event.duration || 50));
  };

  const generateOTP = async () => {
    setIsGenerating(true);
    setEvents([]);
    const startTime = Date.now();

    try {
      // Step 1: Entropy Source
      await logEvent({
        layer: 'entropy',
        type: 'info',
        message: 'Initializing entropy source',
        details: {
          source: 'crypto.getRandomValues',
          browser: navigator.userAgent.split(' ').pop(),
          timestamp: Date.now()
        }
      });

      // Step 2: Get random bytes
      await logEvent({
        layer: 'crypto',
        type: 'process',
        message: 'Requesting cryptographically secure random bytes',
        details: {
          algorithm: 'CSPRNG',
          bytesRequested: 4,
          source: 'Operating System RNG'
        },
        duration: 100
      });

      // Generate random array
      const array = new Uint32Array(1);
      crypto.getRandomValues(array);

      await logEvent({
        layer: 'crypto',
        type: 'success',
        message: 'Random bytes generated',
        details: {
          raw: array[0],
          binary: array[0].toString(2).padStart(32, '0'),
          hex: array[0].toString(16).toUpperCase(),
          entropy: '32 bits'
        }
      });

      // Step 3: Show the math
      await logEvent({
        layer: 'math',
        type: 'process',
        message: 'Converting to 6-digit range',
        details: {
          formula: '(randomValue % 900000) + 100000',
          randomValue: array[0],
          modulo: array[0] % 900000,
          result: (array[0] % 900000) + 100000
        },
        duration: 75
      });

      // Calculate the code
      const code = ((array[0] % 900000) + 100000).toString();

      await logEvent({
        layer: 'format',
        type: 'process',
        message: 'Formatting code',
        details: {
          raw: (array[0] % 900000) + 100000,
          formatted: code,
          digits: code.split('').map((d, i) => ({ position: i + 1, digit: d }))
        }
      });

      // Step 4: Validation
      await logEvent({
        layer: 'validate',
        type: 'process',
        message: 'Validating code properties',
        details: {
          length: code.length === 6 ? '✓ 6 digits' : '✗ Wrong length',
          range: parseInt(code) >= 100000 && parseInt(code) <= 999999 ? '✓ In range' : '✗ Out of range',
          noLeadingZero: code[0] !== '0' ? '✓ No leading zero' : '✗ Has leading zero',
          uniqueDigits: new Set(code).size,
          pattern: code.match(/(\d)\1{2,}/) ? '⚠ Repeated digits' : '✓ Good distribution'
        }
      });

      // Calculate entropy
      const entropy = Math.log2(900000);

      await logEvent({
        layer: 'crypto',
        type: 'info',
        message: 'Entropy calculation',
        details: {
          possibleValues: 900000,
          bitsOfEntropy: entropy.toFixed(2),
          probability: '1 in 900,000',
          bruteForceTime: 'At 1000 attempts/sec: ~15 minutes'
        }
      });

      // Final success
      await logEvent({
        layer: 'format',
        type: 'success',
        message: `✅ OTP Generated: ${code}`,
        details: {
          code,
          generatedAt: new Date().toISOString(),
          validFor: '10 minutes',
          attemptLimit: 3
        }
      });

      setCurrentCode(code);
      setStats(prev => ({
        totalGenerated: prev.totalGenerated + 1,
        averageTime: ((prev.averageTime * prev.totalGenerated) + (Date.now() - startTime)) / (prev.totalGenerated + 1),
        entropy: entropy,
        lastCode: code
      }));

    } catch (error) {
      await logEvent({
        layer: 'crypto',
        type: 'info',
        message: 'Generation failed',
        details: { error: error instanceof Error ? error.message : 'Unknown error' }
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const generateMultiple = async () => {
    setEvents([]);
    const codes: string[] = [];
    const startTime = Date.now();

    await logEvent({
      layer: 'entropy',
      type: 'info',
      message: 'Generating 10 codes for analysis',
      details: { count: 10 }
    });

    for (let i = 0; i < 10; i++) {
      const array = new Uint32Array(1);
      crypto.getRandomValues(array);
      const code = ((array[0] % 900000) + 100000).toString();
      codes.push(code);

      await logEvent({
        layer: 'random',
        type: 'detail',
        message: `Code ${i + 1}: ${code}`,
        details: {
          index: i + 1,
          code,
          raw: array[0]
        },
        duration: 20
      });
    }

    // Analysis
    const digitFrequency: Record<string, number> = {};
    codes.join('').split('').forEach(d => {
      digitFrequency[d] = (digitFrequency[d] || 0) + 1;
    });

    await logEvent({
      layer: 'validate',
      type: 'success',
      message: 'Distribution analysis complete',
      details: {
        codes,
        totalDigits: codes.length * 6,
        digitFrequency,
        timeElapsed: `${Date.now() - startTime}ms`,
        duplicates: codes.length !== new Set(codes).size ? 'Found' : 'None'
      }
    });
  };

  const getLayerColor = (layer: string) => {
    const colors: Record<string, string> = {
      entropy: 'text-purple-400',
      random: 'text-blue-400',
      crypto: 'text-yellow-400',
      math: 'text-green-400',
      format: 'text-cyan-400',
      validate: 'text-pink-400'
    };
    return colors[layer] || 'text-gray-400';
  };

  const getLayerIcon = (layer: string) => {
    const icons: Record<string, string> = {
      entropy: '🎲',
      random: '🔀',
      crypto: '🔐',
      math: '🧮',
      format: '📝',
      validate: '✓'
    };
    return icons[layer] || '•';
  };

  return (
    <div className="min-h-screen bg-black text-green-400 font-mono">
      <div className="max-w-7xl mx-auto p-4">
        {/* Header */}
        <div className="mb-6 border border-green-400 p-4">
          <h1 className="text-3xl mb-2">🎲 OTP GENERATOR - PART 1</h1>
          <p className="text-gray-400">See the journey from entropy to 6-digit code</p>
          <p className="text-yellow-400 text-sm mt-2">Watch randomness become structure</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Left: Controls and Display */}
          <div className="space-y-4">
            {/* Code Display */}
            <div className="border border-green-400 p-4">
              <h2 className="text-xl mb-4 text-green-400">🔢 GENERATED CODE</h2>

              {currentCode ? (
                <div className="space-y-4">
                  <div className="bg-gray-900 border border-green-400 p-8 text-center">
                    <div className="text-6xl font-bold text-green-400 tracking-[1rem]">
                      {currentCode}
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-xs">
                    {currentCode.split('').map((digit, i) => (
                      <div key={i} className="bg-gray-800 p-2 text-center">
                        <div className="text-gray-500">Position {i + 1}</div>
                        <div className="text-2xl text-green-400">{digit}</div>
                        <div className="text-gray-600">0x{digit.charCodeAt(0).toString(16)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="bg-gray-900 border border-gray-600 p-8 text-center">
                  <div className="text-2xl text-gray-600">No code generated yet</div>
                  <div className="text-sm text-gray-700 mt-2">Click "Generate OTP" to begin</div>
                </div>
              )}
            </div>

            {/* Controls */}
            <div className="border border-green-400 p-4">
              <h2 className="text-xl mb-4">🎮 CONTROLS</h2>

              <div className="space-y-2">
                <button
                  onClick={generateOTP}
                  disabled={isGenerating}
                  className="w-full bg-green-400 text-black p-3 hover:bg-green-300 disabled:opacity-50 font-bold"
                >
                  {isGenerating ? '🔄 GENERATING...' : '🎲 GENERATE OTP'}
                </button>

                <button
                  onClick={generateMultiple}
                  disabled={isGenerating}
                  className="w-full bg-blue-400 text-black p-3 hover:bg-blue-300 disabled:opacity-50"
                >
                  📊 GENERATE 10 (Analysis)
                </button>

                <button
                  onClick={() => {
                    setEvents([]);
                    setCurrentCode('');
                  }}
                  className="w-full bg-gray-700 text-white p-3 hover:bg-gray-600"
                >
                  🔄 CLEAR
                </button>
              </div>
            </div>

            {/* Statistics */}
            <div className="border border-green-400 p-4">
              <h2 className="text-xl mb-4">📊 STATISTICS</h2>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Total Generated:</span>
                  <span className="text-green-400">{stats.totalGenerated}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Average Time:</span>
                  <span className="text-green-400">{stats.averageTime.toFixed(0)}ms</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Entropy Bits:</span>
                  <span className="text-green-400">{stats.entropy.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Possible Codes:</span>
                  <span className="text-green-400">900,000</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Collision Chance:</span>
                  <span className="text-green-400">0.0001%</span>
                </div>
              </div>
            </div>

            {/* Security Info */}
            <div className="border border-yellow-400 p-4">
              <h2 className="text-xl mb-4 text-yellow-400">🔒 SECURITY</h2>
              <div className="text-xs text-gray-400 space-y-1">
                <div>• Uses crypto.getRandomValues()</div>
                <div>• Hardware RNG when available</div>
                <div>• 19.77 bits of entropy</div>
                <div>• Unpredictable sequence</div>
                <div>• No patterns or bias</div>
              </div>
            </div>
          </div>

          {/* Right: Event Stream */}
          <div className="border border-green-400 p-4">
            <h2 className="text-xl mb-4">📡 GENERATION EVENT STREAM</h2>

            <div className="h-[700px] overflow-y-auto space-y-2 text-xs font-mono">
              {events.length === 0 ? (
                <div className="text-gray-600 text-center py-8">
                  Waiting for generation events...
                </div>
              ) : (
                events.map(event => (
                  <div
                    key={event.id}
                    className={`border-l-2 pl-2 ${
                      event.type === 'success' ? 'border-green-400' :
                      event.type === 'process' ? 'border-blue-400' :
                      event.type === 'detail' ? 'border-purple-400' :
                      'border-gray-400'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-lg">{getLayerIcon(event.layer)}</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">
                            [{new Date(event.timestamp).toLocaleTimeString('en-US', {
                              hour12: false,
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                              fractionalSecondDigits: 3
                            })}]
                          </span>
                          <span className={`font-bold ${getLayerColor(event.layer)}`}>
                            {event.layer.toUpperCase()}
                          </span>
                        </div>
                        <div className="text-gray-300 mt-1">{event.message}</div>
                        {event.details && (
                          <pre className="text-gray-500 mt-2 bg-gray-900 p-2 rounded overflow-x-auto">
{JSON.stringify(event.details, null, 2)}
                          </pre>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={eventsEndRef} />
            </div>
          </div>
        </div>

        {/* Bottom: The Complete Picture */}
        {currentCode && (
          <div className="mt-6 border border-yellow-400 p-4">
            <h2 className="text-xl text-yellow-400 mb-2">🎯 THE GENERATION COMPLETE!</h2>
            <p className="text-gray-400 mb-4">
              You just witnessed {events.length} events across 6 layers of random number generation,
              from hardware entropy source through cryptographic APIs, mathematical transformations,
              and validation. This is how a truly random, secure OTP code is born!
            </p>
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-purple-400 font-bold">Entropy Source:</span>
                <div className="text-gray-400">
                  • Hardware RNG<br />
                  • OS entropy pool<br />
                  • Crypto API
                </div>
              </div>
              <div>
                <span className="text-yellow-400 font-bold">Cryptographic:</span>
                <div className="text-gray-400">
                  • CSPRNG used<br />
                  • 32 bits generated<br />
                  • Uniform distribution
                </div>
              </div>
              <div>
                <span className="text-green-400 font-bold">Mathematical:</span>
                <div className="text-gray-400">
                  • Modulo operation<br />
                  • Range mapping<br />
                  • No bias introduced
                </div>
              </div>
              <div>
                <span className="text-cyan-400 font-bold">Final Code:</span>
                <div className="text-gray-400">
                  • 6 digits<br />
                  • No leading zero<br />
                  • Ready to use
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}